import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTripContext } from './TripDetailPage';
import { useAiPanel } from '../../components/ai/AiPanelContext';
import { photosApi } from '../../api/photos.api';
import { journalApi } from '../../api/journal.api';
import type { JournalEntry, TripPhoto } from '../../types';
import { toast } from '../../components/ui/Toast';
import LoadingScreen from '../../components/ui/LoadingScreen';
import AlbumSection from '../../components/album/AlbumSection';

/** ADR-0046: the album is a section of its own beside the Diary — made from it, not a way to look at it. */
export default function AlbumTab() {
  const { trip, days } = useTripContext();
  useAiPanel({ context: 'general' });
  const { t } = useTranslation();

  const [photos, setPhotos] = useState<TripPhoto[]>([]);
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([photosApi.list(trip.id), journalApi.list(trip.id)])
      .then(([p, e]) => { setPhotos(p); setEntries(e); })
      .catch(() => toast(t('journal.loadError'), 'error'))
      .finally(() => setLoading(false));
  }, [trip.id]);

  return (
    <div className="flex flex-col h-full overflow-hidden animate-fade-in">
      <div className="shrink-0 px-4 md:px-8 pt-5 pb-4 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800">
        <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400 mb-0.5">
          {trip.destination.toUpperCase()} · {days.length} {t('itinerary.dayUnit', { count: days.length })}
        </p>
        <h2 className="font-serif italic font-semibold text-2xl md:text-3xl text-slate-900 dark:text-slate-100">
          {t('trip.nav.album')}
        </h2>
      </div>

      {/* The album is a workspace: on wide screens it fills the tab and its columns scroll on their own. */}
      <div className="flex-1 px-4 md:px-8 min-h-0 overflow-auto md:overflow-hidden py-4">
        {loading ? <LoadingScreen /> : <AlbumSection tripId={trip.id} photos={photos} entries={entries} days={days} />}
      </div>
    </div>
  );
}
